
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Flex,
  Typography,
  Form,
  Input,
  Radio,
  Select,
  Checkbox,
  DatePicker,
  Switch,
  List,
  Avatar,
  Button,
  Grid,
} from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import { accountService } from '../../services/accountService';
import { ProductItem } from '../../types';
import * as AntIcons from '@ant-design/icons';
import moment from 'moment';
import { AntdIconProps } from '../../types';


const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;


const DynamicAntdIcon = ({ iconName, props }: AntdIconProps) => {
  const IconComponent = React.useMemo(() => {
    return (AntIcons as unknown as Record<string, React.ComponentType>)[iconName] || AntIcons.QuestionOutlined;
  }, [iconName]);

  return <IconComponent {...props} />;
};

interface AccountModalProps {
  isModalVisible: boolean;
  handleCancel: () => void;
  handleSubmit: (values: any, modules: ProductItem[]) => Promise<void>;
  initialData?: any | null;
  isEditing?: boolean;
}

export const AccountModal: React.FC<AccountModalProps> = ({
  isModalVisible,
  handleCancel,
  handleSubmit,
  initialData = null,
  isEditing = false,
}) => {
  const [form] = Form.useForm();
  const [selectedAccountType, setSelectedAccountType] = useState<'buyer' | 'seller'>('seller');
  const [modules, setModules] = useState<ProductItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    const loadModules = async () => {
      const resultModules = await accountService.getAllModules();
      if (resultModules) {
        const modulesWithEnabled = resultModules.map((module: ProductItem) => {
          // If editing, check if the module is in the account's products
          const accountModule = isEditing && initialData?.products 
            ? initialData.products.find((p: any) => p.id === module.id) 
            : null;
          
          return {
            ...module,
            isEnabled: accountModule ? accountModule.isEnabled : true, // Default to enabled for creation
          };
        });
        setModules(modulesWithEnabled);
      }
    };

    if (isModalVisible) {
      loadModules();
      if (isEditing && initialData) {
        // Populate form for editing
        form.setFieldsValue({
          companyName: initialData.companyName,
          accountType: initialData.type,
          contactEmail: initialData.email,
          monthlyBaseFee: initialData.billingPlan?.monthlyBaseFee,
          includedConversations: initialData.billingPlan?.conversationLimit,
          extraConversationFee: initialData.billingPlan?.extraConversationFee,
          effectiveFrom: initialData.billingPlan?.effectiveFrom ? moment(initialData.billingPlan.effectiveFrom, 'YYYY-MM') : null,
        });
        setSelectedAccountType(initialData.type);
      } else {
        // Reset for creation
        form.resetFields();
        form.setFieldsValue({ accountType: 'seller' });
        setSelectedAccountType('seller');
      }
    }
  }, [isModalVisible, isEditing, initialData, form]);

  const onAccountTypeChange = (e: any) => {
    const newType = e.target.value;
    setSelectedAccountType(newType);
    form.setFieldsValue({ accountType: newType });
  };

  const toggleProduct = (id: string, isEnabled: boolean) => {
    setModules((prev) =>
      prev.map((module) => (module.id === id ? { ...module, isEnabled } : module))
    );
  };

  const onFinish = async () => {
    setIsSubmitting(true);
    try {
      const values = await form.validateFields();
      await handleSubmit(values, modules);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProductItem = (item: ProductItem) => {
    if (item.isEnabled === undefined) {
      item.isEnabled = true;
    }
    return (
      <List.Item
        key={item.id}
        extra={
          <Switch
            checked={item.isEnabled}
            onChange={(checked) => toggleProduct(item.id, checked)}
            size={isMobile ? 'small' : 'default'}
          />
        }
      >
        <List.Item.Meta
          avatar={
            <Avatar
              icon={<DynamicAntdIcon iconName={item.icon} />}
              style={{
                width: '40px',
                height: '40px',
                background: 'var(--ant-colorPrimary)',
                color: '#fff'
              }}
            />
          }
          title={<Text strong>{item.name}</Text>}
          description={<Text type="secondary">{item.description}</Text>}
        />
      </List.Item>
    );
  };

  return (
    <Modal
      open={isModalVisible}
      onCancel={handleCancel}
      footer={null}
      centered
      closable={true}
      maskClosable={true}
      width={isMobile ? '90%' : 600}
      styles={{
        body: { padding: 0 },
        mask: { backgroundColor: 'rgba(0, 0, 0, 0.25)' },
        content: { padding: '0px' },
      }}
    >
      <Card style={{ background: 'var(--ant-colorBgContainer)', border: 'none', maxHeight: '95vh', overflowY: 'auto' }}>
        <Flex vertical gap={'16px'}>
          <Title level={4} style={{ marginBottom: 0 }}>
            {isEditing ? 'Edit account' : 'Create new account'}
          </Title>
          <Paragraph type="secondary">
            {isEditing
              ? 'Edit the account details below. You can manage permissions and products from the account settings.'
              : 'Set up a client account and select which modules to enable. You can manage permissions and products later from the account settings.'}
          </Paragraph>
          <Form form={form} layout="vertical">
            <div style={{ background: 'var(--ant-layoutBg)', padding: '24px', borderRadius: '4px' }}>
              <Form.Item
                name="companyName"
                label="Company name"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input placeholder="Enter company name" />
              </Form.Item>

              <Form.Item
                name="accountType"
                label="Account type"
                rules={[{ required: true, message: 'Please select account type' }]}
                initialValue="seller"
              >
                <Radio.Group onChange={onAccountTypeChange}>
                  <Radio value="buyer">Buyer</Radio>
                  <Radio value="seller">Seller</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="contactEmail"
                label={
                  <span>
                    Contact email <span style={{ color: '#21212F45' }}>(optional)</span>
                  </span>
                }
              >
                <Input placeholder="Enter contact email" />
              </Form.Item>

              {selectedAccountType === 'buyer' && (
                <>
                  <Form.Item
                    name="assignSeller"
                    label={
                      <span>
                        Assign Seller <span style={{ color: '#21212F45' }}>(optional)</span>
                      </span>
                    }
                  >
                    <Select
                      placeholder="Select a seller"
                      suffixIcon={<CaretDownOutlined />}
                    >
                      <Select.Option value="seidor">Seidor</Select.Option>
                    </Select>
                  </Form.Item>

                  <div style={{ marginBottom: '24px' }}>
                    <Title level={5} style={{ marginBottom: '4px' }}>
                      Products to enable
                    </Title>
                    <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
                      All products are enabled by default. Uncheck any you don't want to activate for this account.
                    </Paragraph>

                    {modules.filter(module => !module.isComingSoon).map((module) => (
                      <div key={module.id} style={{
                        background: 'white',
                        padding: '12px',
                        borderRadius: '4px',
                        marginBottom: '4px',
                      }}>
                        <Title level={5} style={{ marginBottom: '4px', fontSize: '14px', fontWeight: 600 }}>
                          Customer Service
                        </Title>
                        <Checkbox
                          checked={module.isEnabled}
                          onChange={(e) => toggleProduct(module.id, e.target.checked)}
                          style={{
                            background: 'var(--ant-layoutBg)',
                            padding: '12px',
                            borderRadius: '4px',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            marginTop: '4px',
                          }}
                        >
                          {module.name}
                        </Checkbox>
                      </div>
                    ))}
                  </div>

                  <div>
                    <Title level={5} style={{ marginBottom: '24px' }}>
                      Billing Plan
                    </Title>

                    <Form.Item name="monthlyBaseFee" label="Monthly Base Fee" style={{ marginBottom: '24px' }}>
                      <Input prefix="$" placeholder="0.00" />
                    </Form.Item>

                    <Form.Item name="includedConversations" label="Included Conversations" style={{ marginBottom: '24px' }}>
                      <Input placeholder="1000" />
                    </Form.Item>

                    <Form.Item name="extraConversationFee" label="Extra Conversation Fee" style={{ marginBottom: '24px' }}>
                      <Input prefix="$" placeholder="0.00" />
                    </Form.Item>

                    <Form.Item name="effectiveFrom" label="Effective From" style={{ marginBottom: 0 }}>
                      <DatePicker picker="month" style={{ width: '100%' }} placeholder="Select month and year" />
                    </Form.Item>
                  </div>
                </>
              )}
            </div>

            <Flex justify="flex-end" gap={'8px'} style={{ marginTop: '24px' }}>
              <Button onClick={handleCancel} disabled={isSubmitting}>Cancel</Button>
              <Button type="primary" onClick={onFinish} loading={isSubmitting}>
                {isEditing ? 'Update' : 'Continue'}
              </Button>
            </Flex>
          </Form>
        </Flex>
      </Card>
    </Modal>
  );
};
